import axios from "axios";
import env from "../variables/config";

axios.defaults.baseURL = env.apiUrl;
const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const errorMessage = (e: any) => e?.response?.data?.message || e?.response?.data?.error || "Une erreur est survenue";

/**
 * /bo/emailing — emails to app users and to prospects without an account (« contacts »), sent
 * through Resend with the account's Resend templates. Backend: admin/emailingAdminController.ts
 */

export type MailCategory = "transactional" | "marketing";
export type MailSendStatus = "queued" | "scheduled" | "canceled" | "sent" | "delivered" | "delayed" | "opened" | "clicked" | "bounced" | "complained" | "failed";
export type UserSegment = "all" | "customers" | "trial" | "trial_ending" | "trial_expired" | "inactive" | "never_emailed" | "opted_out";
/** `user` = has a DuckFlow account · `contact` = prospect without one. */
export type RecipientKind = "user" | "contact";

/** What the composer needs to address someone. */
export interface RecipientRef {
    kind: RecipientKind;
    _id: string;
    email: string;
    name?: string;
}

export const recipientKey = (r: { kind: RecipientKind; _id: string }) => `${r.kind}:${r._id}`;
const toApi = (rs: { kind: RecipientKind; _id: string }[]) => rs.map((r) => ({ kind: r.kind, id: r._id }));

export interface EmailingStatus {
    resend: { ok: boolean; from?: string; missing: string[] };
    repeatWindowDays: number;
    /** Resend programs at most this many days ahead. */
    scheduleMaxDays: number;
    /** Programmed sends are typed and shown in this time zone (Europe/Paris). */
    timezone: string;
}

export interface EmailingOverview {
    days: number;
    users: Record<UserSegment, number>;
    newUsers: number;
    contacts: number;
    emails: { sent: number; recipients: number };
}

export interface EmailStats {
    count: number;
    lastSentAt?: string;
    lastSubject?: string;
    lastTemplate?: string;
}

export interface EmailingUser {
    kind: "user";
    _id: string;
    email: string;
    name: string;
    company?: string;
    isSub: boolean;
    offer: "Premium" | "Essai" | "Essai terminé" | "Sans abonnement";
    trialEndsAt?: string;
    createdAt?: string;
    lastConnection?: string;
    marketingOptOut: boolean;
    emails: EmailStats;
}

export interface EmailingContact {
    kind: "contact";
    _id: string;
    email: string;
    name: string;
    givenName?: string;
    familyName?: string;
    company?: string;
    note?: string;
    createdAt?: string;
    marketingOptOut: boolean;
    emails: EmailStats;
}

export type EmailingRecipient = EmailingUser | EmailingContact;

export interface MailTemplate {
    _id: string;
    name: string;
    category: MailCategory;
    subject: string;
    resend: { id: string; from?: string; replyTo?: string; hasUnpublished?: boolean };
    freeVariables: string[];
}

export interface MailSend {
    _id: string;
    to: string;
    toName?: string;
    templateId?: string;
    templateName: string;
    category: MailCategory;
    subject: string;
    html?: string;
    variables?: Record<string, string>;
    sentByEmail?: string;
    replyTo?: string;
    status: MailSendStatus;
    /** Programmed send: when Resend sends it (also its `createdAt`). */
    scheduledFor?: string;
    error?: string;
    events?: { type: string; at: string; detail?: string }[];
    createdAt: string;
}

export interface Preview {
    subject: string;
    html: string;
    missing: string[];
}

export interface Skipped {
    kind: RecipientKind;
    id: string;
    email?: string;
    name?: string;
    reason: "no_email" | "opted_out" | "not_found" | "recently_sent";
    lastSentAt?: string;
}

/** Per template id: who of the given recipients already received it, or will (programmed sends still to come). */
export type SentHistory = Record<string, { kind: RecipientKind; id: string; count: number; lastSentAt?: string; scheduledCount: number; nextScheduledAt?: string }[]>;

export const resendTemplateUrl = (id: string) => `https://resend.com/templates/${id}`;

export const getEmailingStatus = async () => (await axios.get("/admin/emailing/status", config())).data as EmailingStatus;
export const getEmailingOverview = async (days = 30) => (await axios.get("/admin/emailing/overview", { ...config(), params: { days } })).data as EmailingOverview;

// ── Users ──
export const getEmailingUsers = async (params: { segment?: UserSegment; q?: string; page?: number; limit?: number; sort?: string }) =>
    (await axios.get("/admin/emailing/users", { ...config(), params })).data as { users: EmailingUser[]; total: number; page: number; totalPages: number };

export const getAllEmailingUsers = async (params: { segment?: UserSegment; q?: string }) =>
    (await axios.get("/admin/emailing/users/ids", { ...config(), params })).data as { users: RecipientRef[]; truncated: boolean };

export const setUserMarketingOptOut = async (id: string, optOut: boolean) => (await axios.patch(`/admin/emailing/users/${id}/marketing`, { optOut }, config())).data;

// ── Prospects ──
export const getEmailingContacts = async (params: { q?: string; page?: number; limit?: number }) =>
    (await axios.get("/admin/emailing/contacts", { ...config(), params })).data as { contacts: EmailingContact[]; total: number; page: number; totalPages: number };

export type ContactFields = { email?: string; givenName?: string; familyName?: string; company?: string; note?: string; marketingOptOut?: boolean };

/** `user` comes back when the address already has a DuckFlow account (409): write to them as a user instead. */
export const createEmailingContact = async (fields: ContactFields): Promise<{ contact?: EmailingContact; existed?: boolean; user?: RecipientRef; error?: string }> => {
    try {
        return (await axios.post("/admin/emailing/contacts", fields, config())).data;
    } catch (e: any) {
        if (e?.response?.status === 409 && e.response.data?.user) return { user: e.response.data.user, error: e.response.data.message };
        return { error: errorMessage(e) };
    }
};

export const updateEmailingContact = async (id: string, fields: ContactFields): Promise<{ contact?: EmailingContact; error?: string }> => {
    try {
        return (await axios.patch(`/admin/emailing/contacts/${id}`, fields, config())).data;
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

export const deleteEmailingContact = async (id: string) => (await axios.delete(`/admin/emailing/contacts/${id}`, config())).data;

// ── One recipient + their history ──
export const getEmailingRecipient = async (kind: RecipientKind, id: string) =>
    (await axios.get(`/admin/emailing/${kind === "user" ? "users" : "contacts"}/${id}`, config())).data as { recipient: EmailingRecipient; sends: MailSend[] };

export const searchRecipients = async (q: string) =>
    (await axios.get("/admin/emailing/recipients/search", { ...config(), params: { q } })).data.recipients as (RecipientRef & { company?: string })[];

// ── Templates, preview, send ──
/** The account's Resend templates (the backend re-syncs them at most once a minute). */
export const getMailTemplates = async () => (await axios.get("/admin/emailing/templates", config())).data.templates as MailTemplate[];

export const getSentHistory = async (recipients: RecipientRef[]) =>
    (await axios.post("/admin/emailing/sent-history", { recipients: toApi(recipients) }, config())).data.byTemplate as SentHistory;

export const previewMail = async (body: { templateId: string; recipient?: RecipientRef; variables?: Record<string, string> }) =>
    (await axios.post("/admin/emailing/preview", { ...body, recipient: body.recipient ? toApi([body.recipient])[0] : undefined }, config())).data as Preview;

export const checkRecipients = async (templateId: string, recipients: RecipientRef[]) =>
    (await axios.post("/admin/emailing/check", { templateId, recipients: toApi(recipients) }, config())).data as { sendable: number; skipped: Skipped[]; repeats: Skipped[] };

export interface SendResponse {
    sent?: number;
    /** Set when the send was programmed. */
    scheduledFor?: string;
    failed?: number;
    skipped?: Skipped[];
    /** 409: same template sent recently to these — confirm, then resend with `force`. */
    repeats?: Skipped[];
    error?: string;
}

/** `scheduledAt`: `"2026-10-28T09:00"`, read as Paris time by the backend. */
export const sendMail = async (body: { templateId: string; recipients: RecipientRef[]; variables: Record<string, string>; replyTo?: string; force?: boolean; scheduledAt?: string }): Promise<SendResponse> => {
    try {
        return (await axios.post("/admin/emailing/send", { ...body, recipients: toApi(body.recipients) }, config())).data;
    } catch (e: any) {
        const d = e?.response?.data;
        if (e?.response?.status === 409 && d?.repeats) return { repeats: d.repeats, skipped: d.skipped };
        return { error: errorMessage(e), skipped: d?.skipped, sent: d?.sent, failed: d?.failed };
    }
};

export const sendTestMail = async (body: { templateId: string; recipient?: RecipientRef; variables?: Record<string, string> }): Promise<{ to?: string; error?: string }> => {
    try {
        return (await axios.post("/admin/emailing/test", { ...body, recipient: body.recipient ? toApi([body.recipient])[0] : undefined }, config())).data;
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

export const getMailSend = async (id: string) => (await axios.get(`/admin/emailing/sends/${id}`, config())).data.send as MailSend;

// ── Programmed sends ──
export interface ScheduledSend {
    batchId: string;
    scheduledFor: string;
    templateName: string;
    category: MailCategory;
    sentByEmail?: string;
    count: number;
    /** The first five names. */
    recipients: string[];
}

export const getScheduledSends = async () => (await axios.get("/admin/emailing/scheduled", config())).data.scheduled as ScheduledSend[];

export const cancelScheduledSend = async (batchId: string): Promise<{ canceled?: number; failed?: number; error?: string }> => {
    try {
        return (await axios.post(`/admin/emailing/scheduled/${batchId}/cancel`, {}, config())).data;
    } catch (e) {
        return { error: errorMessage(e) };
    }
};
